import React from "react";
import { Plus, Trash2 } from "lucide-react";

export const CONDITIONS = ["Good", "Needs Repair", "Damaged", "Scrapped"];
export const blankRow = (kind) => ({ kind, category: "", item: "", quantity: 1, condition: "Good", make_brand: "", serial_no: "", purchase_date: "", value: 0, remarks: "" });

const cell = "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#234333] disabled:bg-[#f9f8f6]";
const HEAD = ["#", "Category *", "Item *", "Qty *", "Condition", "Make / Brand", "Serial / Asset Tag", "Purchase Date", "Value (₹)", "Remarks", ""];

function Row({ row, i, kind, cats, itemsByCat, onChange, onRemove, writable }) {
  const items = itemsByCat[row.category] || [];
  const set = (k) => (e) => onChange({ [k]: e.target.value });
  const tid = `ui-${kind}-${i}`;
  return (
    <tr className="border-t border-[#e5e0d8] align-top" data-testid={`${tid}-row`}>
      <td className="px-2 py-2 text-xs text-[#6b655b]">{i + 1}</td>
      <td className="px-1 py-2 min-w-[150px]">
        <select className={cell} value={row.category} disabled={!writable} onChange={(e) => onChange({ category: e.target.value, item: "" })} data-testid={`${tid}-category`}>
          <option value="">Select</option>
          {cats.map((c) => <option key={c}>{c}</option>)}
          {row.category && !cats.includes(row.category) && <option>{row.category}</option>}
        </select>
      </td>
      <td className="px-1 py-2 min-w-[160px]">
        <select className={cell} value={row.item} disabled={!writable || !row.category} onChange={set("item")} data-testid={`${tid}-item`}>
          <option value="">{row.category ? "Select" : "Pick category"}</option>
          {items.map((c) => <option key={c}>{c}</option>)}
          {row.item && !items.includes(row.item) && <option>{row.item}</option>}
        </select>
      </td>
      <td className="px-1 py-2 w-20"><input type="number" min="1" className={cell} value={row.quantity} disabled={!writable} onChange={set("quantity")} data-testid={`${tid}-qty`} /></td>
      <td className="px-1 py-2 min-w-[120px]">
        <select className={cell} value={row.condition} disabled={!writable} onChange={set("condition")} data-testid={`${tid}-condition`}>{CONDITIONS.map((c) => <option key={c}>{c}</option>)}</select>
      </td>
      <td className="px-1 py-2 min-w-[110px]"><input className={cell} maxLength={80} value={row.make_brand} disabled={!writable} onChange={set("make_brand")} data-testid={`${tid}-make`} /></td>
      <td className="px-1 py-2 min-w-[120px]"><input className={cell} maxLength={80} value={row.serial_no} disabled={!writable} onChange={set("serial_no")} data-testid={`${tid}-serial`} /></td>
      <td className="px-1 py-2 min-w-[130px]"><input type="date" className={cell} max={new Date().toISOString().slice(0, 10)} value={row.purchase_date} disabled={!writable} onChange={set("purchase_date")} data-testid={`${tid}-date`} /></td>
      <td className="px-1 py-2 w-28"><input type="number" min="0" step="0.01" className={cell} value={row.value} disabled={!writable} onChange={set("value")} data-testid={`${tid}-value`} /></td>
      <td className="px-1 py-2 min-w-[150px]"><input className={cell} maxLength={300} value={row.remarks} disabled={!writable} onChange={set("remarks")} data-testid={`${tid}-remarks`} /></td>
      <td className="px-1 py-2">
        {writable && <button type="button" onClick={onRemove} className="p-1.5 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 rounded-sm" aria-label="Remove row" data-testid={`${tid}-remove`}><Trash2 size={14} /></button>}
      </td>
    </tr>
  );
}

export default function UnitItemsTable({ kind, title, rows, cats, itemsByCat, onRowsChange, writable }) {
  const update = (i, patch) => onRowsChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const totalQty = rows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
  const totalVal = rows.reduce((s, r) => s + (Number(r.value) || 0), 0);
  return (
    <section className="bg-white border border-[#e5e0d8] rounded-sm" data-testid={`ui-section-${kind}`}>
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#e5e0d8] bg-[#f9f8f6] flex-wrap">
        <div>
          <div className="font-serif text-xl text-[#1c1a17]">{title}</div>
          <div className="text-[11px] text-[#6b655b]" data-testid={`ui-${kind}-totals`}>{rows.length} row(s) · Qty {totalQty} · Value ₹{totalVal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
        </div>
        {writable && (
          <button type="button" onClick={() => onRowsChange([...rows, blankRow(kind)])} className="inline-flex items-center gap-1.5 bg-[#234333] hover:bg-[#1b3326] text-white text-xs font-semibold px-3 py-2 rounded-sm" data-testid={`ui-${kind}-add-row`}>
            <Plus size={13} /> Add Row
          </button>
        )}
      </div>
      {cats.length === 0 && <div className="px-5 py-3 text-xs text-[#9e2a2b]">No {kind} categories in Masters yet. Add them under Masters first.</div>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left">{HEAD.map((h) => <th key={h} className="px-2 py-2 text-[9px] tracking-[0.15em] uppercase text-[#6b655b] font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={HEAD.length} className="px-5 py-6 text-center text-xs text-[#6b655b]" data-testid={`ui-${kind}-empty`}>No {kind} items recorded. {writable && "Click “Add Row” to start."}</td></tr>}
            {rows.map((r, i) => (
              <Row key={i} row={r} i={i} kind={kind} cats={cats} itemsByCat={itemsByCat} writable={writable} onChange={(p) => update(i, p)} onRemove={() => onRowsChange(rows.filter((_, j) => j !== i))} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
